import React, { useState } from 'react';

import { Icon } from 'semantic-ui-react';

interface ScheduleRule {
    day: 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';
    time: string; // "HH:MM"
    duration: number; // minutes
}

interface ScheduleGeneratorProps {
    classId: string;
    className: string;
    blockStartDate: string;
    onClose: () => void;
    onGenerate: (classId: string, rules: ScheduleRule[], totalSessions: number) => void;
}

// Define the initial state for the rules in the modal
const initialRules: ScheduleRule[] = [
    { day: 'Mon', time: '13:00', duration: 60 },
];

const ScheduleGeneratorModal: React.FC<ScheduleGeneratorProps> = ({
    classId,
    className,
    blockStartDate,
    onClose,
    onGenerate
}) => {
    const [rules, setRules] = useState<ScheduleRule[]>(initialRules);
    const [totalSessions, setTotalSessions] = useState<number>(22);

    const DAY_OPTIONS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const updateRule = (index: number, field: keyof ScheduleRule, value: string | number) => {
        setRules(prev => prev.map((rule, i) =>
            i === index ? { ...rule, [field]: value } : rule
        ));
    };

    const addRule = () => {
        setRules(prev => [...prev, { day: 'Mon', time: '09:00', duration: 60 }]);
    };

    const removeRule = (index: number) => {
        setRules(prev => prev.filter((_, i) => i !== index));
    };

    const handleGenerateClick = () => {
        const validRules = rules.filter(r => r.time && r.duration > 0);
        if (validRules.length > 0 && totalSessions > 0) {
            onGenerate(classId, validRules, totalSessions);
            onClose();
        } else {
            alert('Please define at least one valid rule and a total number of sessions.');
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
            display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
            <div style={{
                backgroundColor: 'white', padding: '20px', borderRadius: '8px',
                width: '600px', maxHeight: '90vh', overflowY: 'auto'
            }}>
                <h3>Scheduling for: {className}</h3>
                <p>Block Start Date: <strong>{blockStartDate}</strong></p>

                <label>Total Sessions to Generate:</label>
                <input
                    type="number"
                    value={totalSessions}
                    min="1"
                    onChange={(e) => setTotalSessions(parseInt(e.target.value) || 0)}
                    style={{ marginLeft: '10px', padding: '5px' }}
                />

                <hr />

                <h4>Repeating Weekly Rules</h4>
                {rules.map((rule, index) => (
                    <div key={index} style={{ marginBottom: '10px', border: '1px solid #ccc', padding: '10px' }}>
                        <select
                            value={rule.day}
                            onChange={(e) => updateRule(index, 'day', e.target.value as ScheduleRule['day'])}
                            style={{ padding: '5px', marginRight: '10px' }}
                        >
                            {DAY_OPTIONS.map(day => <option key={day} value={day}>{day}</option>)}
                        </select>

                        <input
                            type="time"
                            value={rule.time}
                            onChange={(e) => updateRule(index, 'time', e.target.value)}
                            style={{ padding: '5px', marginRight: '10px' }}
                        />

                        <input
                            type="number"
                            value={rule.duration}
                            onChange={(e) => updateRule(index, 'duration', parseInt(e.target.value) || 0)}
                            min="15"
                            style={{ width: '80px', padding: '5px', marginRight: '10px' }}
                        /> min

                        <button onClick={() => removeRule(index)} style={{ color: 'red', border: 'none', background: 'none' }}>
                            <Icon name="minus circle" /> Remove
                        </button>
                    </div>
                ))}

                <button onClick={addRule} style={{ marginBottom: '20px', padding: '5px 10px' }}>
                    <Icon name="plus circle" /> Add Rule
                </button>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button onClick={onClose} style={{ padding: '10px 15px', backgroundColor: '#eee' }}>Cancel</button>
                    <button onClick={handleGenerateClick} style={{ padding: '10px 15px', backgroundColor: '#10b981', color: 'white' }}>
                        Generate & Replace Sessions
                    </button>
                </div>
            </div>
        </div>
    );
};

export { ScheduleGeneratorModal };
